import api from 'api';
import { diff } from 'deep-object-diff';
import enums from 'utilities/enums';

export const RCUDefaultValues = {
  generalSettings: { clientEmail: '', domain: undefined, emailSubject: '', emailMessage: '' },
  amlCase: {
    isEnabled: false,
    screeningProfileId: null,
    createCaseAfterAllRequestsAreAccepted: false,
    monitorCase: false,
    autoUpdateStatus: false,
    caseStatus: 0,
  },
  dataCollection: {
    requestPersonalInformation: true,
    personalInformationInputOption: '1',
    requestAddressInformation: true,
    addressInformationInputOption: '0',
    requestAdditionalDetails: true,
    requestProofOfAddress: false,
    additionalDetailsInputOption: '1',
    requestIdentityDocument: false,
    requireTwoPiecesOfId: false,
    requestSupportingDocuments: false,
    packagesTypes: [],
  },
  automation: {
    isEnabled: false,
    identityAuthenticationAutoAcceptance: false,
    documentAuthenticationAutoAcceptance: false,
    biometricAuthenticationAutoAcceptance: false,
    enablePeriodicReview: true,
    periodicReviewInterval: 'Years',
    periodicReviewValue: 2,
    setProfileStatus: true,
    status: 1,
    setRiskLevel: true,
    riskLevel: 2,
    automationsJurisdictions: [],
  },
  comment: { isEnabled: false, commentBody: '' },
  kycRequests: {
    allEnabled: false,
    runVerification: false,
    identityVerification: {
      isEnabled: false,
      runIdentityDataConfirmation: false,
      livenessDetection: false,
    },

    documentAuthentication: {
      isEnabled: false,
      templateVerification: false,
      documentDataVerification: false,
      includeFaceMatch: false,
      faceMatchStrategy: '1',
    },
    biometricAuthentication: {
      isEnabled: false,
      faceMatchType: '0',
    },
  },
};

/**
 * Creates a hidden package if it is needed. It checks if the hidden package exists and if the documents have changed.
 * If the documents have not changed, it returns the existing package. If the documents have changed or there is no hidden package, it creates a new one.
 *
 * @param {Object} dataCollection - The collection of data to be used for creating the hidden package.
 * @param {Object} isNaturalPerson - Whether the entity is a natural person.
 * @param {string} portalId - The ID of the portal.
 * @param {Object} hiddenPackageInfo - The information about the existing hidden package.
 * @param {Function} setHiddenPackageInfo - A function to set the hidden package information.
 * @returns {Promise<Object>} A promise that resolves to the created hidden package.
 */

export const createHiddenPackageIfNeeded = async (
  dataCollection,
  isNaturalPerson,
  portalId,
  hiddenPackageInfo,
  setHiddenPackageInfo,
) => {
  const docsChanged =
    hiddenPackageInfo && Object.keys(diff(hiddenPackageInfo.originalDocs, dataCollection.packagesTypes)).length > 0;

  // If we have a hidden package and docs haven't changed, return the existing one
  if (hiddenPackageInfo && !docsChanged) {
    return hiddenPackageInfo.package;
  }

  // If we have packages to process and either no hidden package exists or docs have changed
  if (dataCollection.packagesTypes?.length > 0) {
    const packageName = dataCollection.packagesTypes.map(({ package: { name } }) => name).join(', ');
    const {
      data: { id: hiddenPackageId, ...restHidden },
    } = await api.administration.supportingDocumentPackages.createHiddenSupportingDocumentPackage({
      header: packageName,
      entityType: isNaturalPerson ? enums.ENTITY_TYPE_NAMES.NATURAL_PERSON : enums.ENTITY_TYPE_NAMES.LEGAL_ENTITY,
    });

    const packageItems = [];

    for (const supportingDocument of dataCollection.packagesTypes) {
      const handleCreateHiddenPackageDocument = async values => {
        console.log({ values });
        const {
          package: { document, documentPath, parameters, documentFullPath, documentOriginalName, ...rest },
          required,
        } = values;

        const formData = new FormData();

        if (documentOriginalName && documentFullPath) {
          try {
            const response = await fetch(documentFullPath);
            if (!response.ok) {
              throw new Error('Failed to fetch the document');
            }

            const blob = await response.blob();

            // Create a File object with the original name and type
            const file = new File([blob], documentOriginalName, { type: blob.type });

            // Append to FormData
            formData.set('document', file);

            // Now you can send this formData via fetch or axios
            // await fetch('/your-endpoint', { method: 'POST', body: formData });
          } catch (error) {
            console.error('Error fetching or uploading the file:', error);
          }
        }

        formData.set(
          'item',
          JSON.stringify({
            ...rest,
            parameters: parameters,
            documentOriginalName: document?.name,
            isRequired: required,
          }),
        );

        return await api.administration.supportingDocumentPackages.createHiddenSupportingDocumentPackageDocument(
          hiddenPackageId,
          formData,
        );
      };

      const { data: packageItem } = await handleCreateHiddenPackageDocument(supportingDocument);
      packageItems.push(packageItem);
    }

    await api.administration.linkHiddenPackageToPortal(
      {
        ...restHidden,
        id: hiddenPackageId,
        items: packageItems,
      },
      portalId,
    );

    const newHiddenPackage = {
      assignedUserId: dataCollection.packagesTypes[0]?.assignedUserId,
      packageId: hiddenPackageId,
      required: true,
    };
    setHiddenPackageInfo({
      package: newHiddenPackage,
      originalDocs: dataCollection.packagesTypes,
    });

    return newHiddenPackage;
  }

  return null;
};
